import * as _ from 'underscore';
import { FeatureFlags } from '../FeatureFlags';
import { L10n } from '../L10n/L10n';
import { IGranteePermissions } from '../models/IGranteePermissions';
import { IPermission, Permission, PermissionReason } from '../models/Permission';
import { GetEffectivePermissionsRequest } from '../ServerApi/requests/GetEffectivePermissionsRequest';
import { GetExplicitPermissionsRequest } from '../ServerApi/requests/GetExplicitPermissionsRequest';
import { SetExplicitPermissionsRequest } from '../ServerApi/requests/SetExplicitPermissionsRequest';
import { IAuthorizableGranteePermissions } from '../ServerApi/types/IAuthorizableGranteePermissions';
import { ICapabilityValue } from '../ServerApi/types/ICapabilityValue';
import { ITypedId } from '../ServerApi/types/ITypedId';
import { ITypedIds } from '../ServerApi/types/ITypedIds';
import { RequestHelper } from './RequestHelper';

export module PermissionsHelper {
  export function granteeType(row: IGranteePermissions): string {
    if (row.group) {
      return 'group';
    } else if (row.user) {
      return 'user';
    } else {
      return '';
    }
  }

  export function granteeName(row: IGranteePermissions): string {
    if (row.group) {
      if (row.group.userCount >= 0) {
        // TODO: It would be better if this was a localized string replacement,
        // but that would require some minor refactoring, because this static
        // method doesn't have easy access to $translate.
        return row.group.name + ' (' + row.group.userCount + ')';
      } else {
        return row.group.name;
      }
    } else if (row.user) {
      return row.user.displayName;
    } else {
      return '';
    }
  }

  export function hasSameGrantee(a: IGranteePermissions, b: IGranteePermissions): boolean {
    if (a && b) {
      if (a.group && b.group) {
        return a.group.id === b.group.id;
      } else if (a.user && b.user) {
        return a.user.id === b.user.id;
      }
    }
    return false;
  }

  export function compareGrantees(a: IGranteePermissions, b: IGranteePermissions): number {
    if (a.group) {
      if (b.group) { // a = group, b = group
        if (a.group.id === b.group.id){
          return 0;
        } else if (a.group.isAllUsersGroup) { // 'All Users' should be the first group in the list
          return -1;
        } else if (b.group.isAllUsersGroup) {
          return 1;
        } else {
          return compareStrings(a.group.name, b.group.name);
        }
      } else { // a = group, b = user
        return -1;
      }
    } else {
      if (b.group) { // a = user, b = group
        return 1;
      } else { // a = user, b = user
        return compareStrings(a.user.displayName, b.user.displayName);
      }
    }
  }

  function compareStrings(a: string, b: string): number {
    return a.localeCompare(b, L10n.language.replace('_','-'));
  }

  export function permissionFromIGranteePermission(
      igp: IGranteePermissions, contentType: string, capability: string): IPermission {
    const permissions = permissionsFromIGranteePermission(igp, contentType);
    return permissions && permissions[capability];
  }

  export function permissionsFromIGranteePermission(
      igp: IGranteePermissions, contentType: string): _.Dictionary<IPermission> {
    return igp && igp.permissionsByContentType && igp.permissionsByContentType[contentType];
  }

  export function capabilityReason(
      row: IGranteePermissions,
      contentType: string,
      capability: string,
      defaultValue: string
  ): string {
    const permission = permissionFromIGranteePermission(row, contentType, capability);
    if (permission) {
      switch (permission.reason) {
        case PermissionReason.GroupAllow:                   return 'group_allow';
        case PermissionReason.GroupDeny:                    return 'group_disallow';
        case PermissionReason.GroupProjectLeaderAllow:      return 'group_project_leader';
        case PermissionReason.GuestAllow:                   return 'guest_allowed';
        case PermissionReason.DerivedAllow:                 return 'derived_allow';
        case PermissionReason.DerivedWriteAllow:            return 'derived_allow';
        case PermissionReason.ImplicitDeny:                 return 'implicit_deny';
        case PermissionReason.Mixed:                        return 'mixed';
        case PermissionReason.Unspecified:                  return 'unspecified';
        case PermissionReason.UserAdminAllow:               return 'user_admin';
        case PermissionReason.UserAllow:                    return 'user_allow';
        case PermissionReason.UserAuthorizableOwnerAllow:   return 'user_authorizable_owner';
        case PermissionReason.UserDeny:                     return 'user_disallow';
        case PermissionReason.UserLicensingDeny:            return 'user_licensing';
        case PermissionReason.UserProjectLeaderAllow:       return 'user_project_leader';
        case PermissionReason.UserProjectOwnerAllow:        return 'user_project_owner';
        default:                                            return defaultValue;
      }
    } else {
      return defaultValue;
    }
  }

  export function capabilityValue(
      row: IGranteePermissions,
      contentType: string,
      capability: string,
      defaultValue: string
  ): string {
    const permission = permissionFromIGranteePermission(row, contentType, capability);
    if (permission) {
      switch (permission.permission) {
        case Permission.Allowed: return 'allowed';
        case Permission.Denied: return 'denied';
        case Permission.Mixed: return 'mixed';
        default: return defaultValue;
      }
    } else {
      return defaultValue;
    }
  }

  export function capabilityTitleKey(
      row: IGranteePermissions,
      contentType: string,
      capability: string,
      defaultValue: string
  ): string {
    const curr = permissionFromIGranteePermission(row, contentType, capability);
    if (curr && curr.reason !== PermissionReason.Mixed && curr.reason !== PermissionReason.Unspecified) {
      // we have a reason for this capability; it's neither mixed nor unspecified, display the reason
      return 'permission_reason_' + capabilityReason(row, contentType, capability, defaultValue);
    } else {
      // we either don't have a reason, or the reason is mixed or unspecified, display only the value
      return 'permission_value_' + capabilityValue(row, contentType, capability, defaultValue);
    }
  }

  export function pluckPermissionsFromDictionary(input: _.Dictionary<IPermission>): _.Dictionary<Permission> {
    const result: _.Dictionary<Permission> = {};
    _.each(input, (value: IPermission, key: string) => {
      result[key] = value.permission;
    });
    return result;
  }

  // returns a new dictionary of IPermissions with all reasons set to 'allowed'
  export function AllowedIPermissions(permissions: _.Dictionary<IPermission>, reason: PermissionReason): _.Dictionary<IPermission> {
    const allowedPermissions: _.Dictionary<IPermission> = {};
    _.each(permissions, (permission: IPermission, key: any) => {
      allowedPermissions[key] = {
        permission: Permission.Allowed,
        reason,
      };
    });
    return allowedPermissions;
  }

  // returns a new dictionary of IPermissions with all reasons set to 'unspecified'
  export function IPermissionsFromPermissions(permissions: _.Dictionary<Permission>): _.Dictionary<IPermission> {
    const permissionsWithUnspecifiedReasons: _.Dictionary<IPermission> = {};
    _.each(permissions, (permission: Permission, key: any) => {
      permissionsWithUnspecifiedReasons[key] = {
        permission: permission,
        reason: PermissionReason.Unspecified
      };
    });
    return permissionsWithUnspecifiedReasons;
  }

  // returns a new dictionary of Permissions
  export function PermissionsFromIPermissions(ipermissions: _.Dictionary<IPermission>): _.Dictionary<Permission> {
    const permissions: _.Dictionary<Permission> = {};
    _.each(ipermissions, (permission: IPermission, key: string) => {
      permissions[key] = permission.permission;
    });
    return permissions;
  }

  const contentTypesForAuthorizableType: _.Dictionary<string[]> = {
    datasource: ['datasource'],
    flow: ['flow'],
    project: ['project', 'projectWorkbookTemplate', 'projectDatasourceTemplate', 'projectDataRoleTemplate'],
    view: ['view'],
    workbook: ['workbook'],
    dataRole: ['dataRole'],
  };

  export function getPermissionsContentTypes(authorizableType: string, flowsEnabled: boolean): string[] {
    const metricsEnabled = FeatureFlags.metricsSpaceEnabled;
    let dataCatalogEnabled = FeatureFlags.dataCatalogPermissionEnabled;
    let contentTypeMap: _.Dictionary<string[]> = contentTypesForAuthorizableType;

    if (flowsEnabled) {
      contentTypeMap = {
          ...contentTypeMap,
          flow: ['flow'],
          project: [...contentTypeMap['project'], 'projectFlowTemplate']
      };
    }

    if (dataCatalogEnabled) {
        contentTypeMap = {
            ...contentTypeMap,
            table: ['table'],
            database: ['database', 'databaseTableTemplate'],

        };
    }

    if (metricsEnabled) {
      contentTypeMap = {
        ...contentTypeMap,
        metric: ['metric'],
        project: [...contentTypeMap['project'], 'projectMetricTemplate']
      };
    }

    if (FeatureFlags.publishedConnectionContentManagementEnabled) {
      contentTypeMap = {
        ...contentTypeMap,
        publishedConnection: ['publishedConnection'],
        project: [...contentTypeMap['project'], 'projectPublishedConnectionTemplate']
      };
    }

    return contentTypeMap[authorizableType];
  }

  export function explicitPermissionsRequestsForAuthorizables(
      authorizables: ITypedIds): GetExplicitPermissionsRequest[] {
    const buildRequestFn = (authorizableIds: string[]) =>
      new GetExplicitPermissionsRequest(
        { authorizables: {type: authorizables.type, ids: authorizableIds} });
    return RequestHelper.buildRequests(authorizables.ids, buildRequestFn);
  }

  export function effectivePermissionsRequestsForAuthorizables(
      userIds: string[], authorizables: ITypedIds): GetEffectivePermissionsRequest[] {
    const buildRequestFn = (authorizableIds: string[]) =>
      new GetEffectivePermissionsRequest(
        { userIds: userIds, authorizables: {type: authorizables.type, ids: authorizableIds} }
    );
    return RequestHelper.buildRequests(authorizables.ids, buildRequestFn);
  }

  export function setExplicitPermissionsRequestsForAuthorizables(
      authorizables: ITypedIds,
      granteePermissions: IGranteePermissions): SetExplicitPermissionsRequest[] {

    const buildRequest = (authorizableIds: string[]) => {
      const agps: IAuthorizableGranteePermissions[] = [];
      _.each(authorizableIds, (authorizableId: string) => {
        _.each(granteePermissions.permissionsByContentType,
          (permissions: _.Dictionary<IPermission>, contentType: string) => {
            // Don't create a request when no permissions have changed
            if (_.isEqual(permissions, {})) return;

            const agp: IAuthorizableGranteePermissions = {
              authorizable: { type: contentType, id: authorizableId },
              grantee: getGrantee(granteePermissions),
              permissions: capabilityValuesFromPermissions(permissions)
            };
            agps.push(agp);
          }
        );
      });

      return new SetExplicitPermissionsRequest({
        permissions: agps
      });
    };

    // Because we push multiple AGPs onto the request for each authorizable ID, we must make sure
    // that the total number of AGPs per request is still less than the maximum allowed block size (TFS 938787)
    const blockSize = Math.floor(RequestHelper.DefaultBlockSize / _.size(granteePermissions.permissionsByContentType));

    return RequestHelper.buildRequests(authorizables.ids, buildRequest, blockSize);
  }

  export function getGrantee(granteePermissions: IGranteePermissions): ITypedId {
    if (granteePermissions.group) {
      return {
        type: 'group',
        id: granteePermissions.group.id
      };
    } else if (granteePermissions.user) {
      return {
        type: 'user',
        id: granteePermissions.user.id
      };
    } else {
      return null;
    }
  }

  export function capabilityValuesFromPermissions(permissions: _.Dictionary<IPermission>): ICapabilityValue[] {
    return _.map(permissions, (permission: IPermission, capability: string) => {
      const capabilityValue: ICapabilityValue = {
        capability: capability,
        value: capabilityValueFromPermission(permission.permission)
      };
      return capabilityValue;
    });
  }

  export function capabilityValueFromPermission(permission: Permission): string {
      switch(permission) {
      case Permission.Allowed: return 'allowed';
      case Permission.Denied: return 'denied';
      case Permission.Mixed: return 'mixed';
      case Permission.Unspecified: return 'unspecified';
      default: return null;
    }
  }

  export function hasProjectLeaderCapability(row: IGranteePermissions) {
    const contentType = 'project';
    const cap = PermissionsHelper.capabilityValue(row, contentType, 'project_leader', 'denied');
    const inheritedCap = PermissionsHelper.capabilityValue(row, contentType, 'inherited_project_leader', 'denied');
    return row == null ? false : cap === 'allowed' || inheritedCap === 'allowed';
  }

}
